import axios from 'axios';
import { openAlert } from '../Util/Util';
import { extractControllerFromUrl, extractFunnelPageIdFromUrl } from '../Util/URLBuilder';

/**
 * Hook to provide form submission functionality
 * @returns {Object} Object containing submitForm and getFormData functions
 */
export const useFormSubmission = () => {
    // Function to get form data as a string
    const getFormData = () => {
        const form = document.getElementById('reg-form');
        if (!form) {
            console.error('Form not found');
            return '';
        }
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    };

    return {
        submitForm,
        getFormData
    };
};

/**
 * Creates and attaches the global checkForm function
 * @param {Function} submitFormImpl - The form submission implementation function
 * @returns {Function} The original checkForm function if it existed
 */
export const createGlobalCheckForm = (submitFormImpl) => {
    // Store original function if it exists
    const originalCheckForm = window.checkForm;
    
    // Create new global function
    window.checkForm = async (formDataStr, setButtonIndicator, openAlertFn, ajaxUrl, enablePayment, submitBtn) => {
        try {
            // Extract form data parameters
            const form = document.getElementById('reg-form');
            
            // Create options object with proper URL parsing
            const options = {
                enablePayment,
                openAlert: openAlertFn,
                ajaxUrl, // Pass the complete URL directly
                // Extract controller and funnel_page_id from the URL
                controller: extractControllerFromUrl(ajaxUrl)
            };
            
            console.log('checkForm options:', { 
                ajaxUrl, 
                controller: options.controller
            });
            
            // Call implementation
            return await submitFormImpl(submitBtn, form, options);
        } catch (error) {
            console.error('Error in checkForm wrapper:', error);
            // Reset button state
            if (setButtonIndicator) {
                setButtonIndicator('off');
            }
            // Show error
            if (openAlertFn) {
                openAlertFn('系統發生錯誤，請稍後再試。');
            }
            return { success: false, error };
        }
    };
    
    return originalCheckForm;
};

/**
 * HookEvents-specific form submission implementation
 * This matches the structure expected in index.js
 * 
 * @param {object} submitButton - The submit button element
 * @param {object} form - The form element
 * @param {object} options - Additional options like funnel_page_id, turnstileToken, etc.
 * @returns {Promise} - Promise that resolves with submission result
 */
export const submitForm = async (submitButton, form, options = {}) => {
    // Extract options with defaults to handle undefined values
    const { 
        funnel_page_id, 
        turnstileToken, 
        ajaxUrl 
    } = options;
    
    // Create a default t function if none is provided
    const defaultT = key => key; // Identity function that returns the key unchanged
    const t = typeof options.t === 'function' ? options.t : defaultT;
    const optionsOpenAlert = options.openAlert;
    
    // Use the translation function properly
    const translate = (key, defaultText, variables = {}) => {
        try {
            return t(key, variables);
        } catch (e) {
            // If t throws an error, fall back to default text with variable replacement
            console.warn('Translation function error:', e);
            let fallbackText = defaultText || key;
            
            // Simple variable replacement for fallback
            Object.keys(variables).forEach(varKey => {
                const placeholder = `{{${varKey}}}`;
                fallbackText = fallbackText.replace(new RegExp(placeholder, 'g'), variables[varKey]);
            });
            
            return fallbackText;
        }
    };
    
    // Use a consistent alert function
    const showAlert = (message) => {
        try {
            // First try the options-provided openAlert
            if (typeof optionsOpenAlert === 'function') {
                optionsOpenAlert(message);
                return;
            }
            
            // Then try window.openAlert
            if (typeof window.openAlert === 'function') {
                // Pass the message directly without translation - 
                // window.openAlert is responsible for translation
                window.openAlert(message, t);
                return;
            }
            
            // Then try SweetAlert if available
            if (typeof Swal !== 'undefined') {
                // Use our translation helper with fallback
                const displayMessage = translate(message, message);
                Swal.fire({
                    title: '',
                    text: displayMessage,
                    icon: 'warning',
                    confirmButtonText: translate('OK', 'OK')
                });
                return;
            }
            
            // Fallback to browser alert if no alert function is available
            console.warn('No alert function available, using native alert:', message);
            alert(message);
        } catch (alertError) {
            console.error('Error showing alert:', alertError);
            // Final fallback if everything else fails
            try {
                alert(message);
            } catch (e) {
                console.error('Even native alert failed:', e);
            }
        }
    };
    
    try {
        // Show loading indicator
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'on');
            submitButton.disabled = true;
        }
        
        // Get form data
        const formData = new FormData(form);
        
        // Ensure _csrfToken is included
        const csrfInput = document.querySelector('input[name="_csrfToken"]');
        let csrfToken = null;
        if (csrfInput && csrfInput.value && !formData.has('_csrfToken')) {
            formData.append('_csrfToken', csrfInput.value);
            csrfToken = csrfInput.value;
            console.log('Member: Added _csrfToken:', csrfToken);
        } else if (csrfInput && csrfInput.value) {
            csrfToken = csrfInput.value;
        }
        
        const formDataStr = new URLSearchParams(formData).toString();
        
        // Ensure Turnstile token is included
        if (turnstileToken) {
            // Add the token to the request headers
            axios.defaults.headers.common['X-Turnstile-Token'] = turnstileToken;
        }
        
        // Helper function to check if a field is mandatory
        const isFieldMandatory = (field) => {
            if (!field) return false;
            // Check HTML5 required attribute
            if (field.hasAttribute('required') && field.getAttribute('required') !== 'false') {
                return true;
            }
            // Check if parent has 'required' class
            let parent = field.closest('.form-group, .fv-row');
            if (parent && parent.classList.contains('required')) {
                return true;
            }
            // Check FormValidation.io if available
            if (window.fv || window.globalFormValidator) {
                const validator = window.globalFormValidator || window.fv;
                if (validator && validator.elements && field.name) {
                    const fieldConfig = validator.elements[field.name];
                    if (fieldConfig && fieldConfig.validators && fieldConfig.validators.notEmpty) {
                        return true;
                    }
                }
            }
            return false;
        };
        
        // Validate required selections first - session checking ("場次 checking")
        const mbrLevelElements = document.querySelectorAll('.mbr-level-id:checked');
        const allMbrLevelElements = document.querySelectorAll('.mbr-level-id');
        const hasMbrLevels = allMbrLevelElements.length > 0;
        const hasHiddenMbrLevelSelect = document.getElementById('hidden-mbr-level-select');
        
        console.log('MbrLevel validation:', {
            hasMbrLevels,
            mbrLevelsChecked: mbrLevelElements.length,
            hasHiddenMbrLevelSelect: !!hasHiddenMbrLevelSelect
        });
        
        // Only check if at least one mbr level field is mandatory
        if (hasMbrLevels && mbrLevelElements.length === 0) {
            // Check if any mbr level field is mandatory
            const isMbrLevelMandatory = Array.from(allMbrLevelElements).some(field => isFieldMandatory(field));
            if (isMbrLevelMandatory) {
                if (submitButton) {
                    submitButton.setAttribute('data-kt-indicator', 'off');
                    submitButton.disabled = false;
                }
                const errorMessage = translate('請選擇會籍', '請選擇會籍');
                showAlert(errorMessage);
                
                // Create a proper error with the message
                const error = new Error(errorMessage);
                error.code = 'MBR_LEVEL_REQUIRED';
                throw error;
            }
        }
        
        if (!hasMbrLevels && !hasHiddenMbrLevelSelect) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = translate('請選擇會籍', '請選擇會籍');
            showAlert(errorMessage);
            
            // Create a proper error with the message
            const error = new Error(errorMessage);
            error.code = 'NO_VALID_MBR_LEVELS';
            throw error;
        }
        
        
        // Always use the provided ajaxUrl directly if available
        // This ensures we us/e exactly the URL constructed by the button component
        const requestUrl = ajaxUrl || null;
        
        if (!requestUrl) {
            console.error('No AJAX URL provided for form submission');
            const errorMessage = translate('提交表單時發生錯誤，請重試。', '提交表單時發生錯誤，請重試。');
            showAlert(errorMessage);
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Create a proper error with the message
            const error = new Error(errorMessage);
            error.code = 'URL_MISSING';
            throw error;
        }
        
        console.log(`Member: Submitting to ${requestUrl}`);
        
        // Prepare headers
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
        };
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }
        
        const response = await axios.post(requestUrl, formDataStr, {
            headers: headers
        });
        
        const data = response.data;
        if (data.require_turnstile) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = data.rm || 'Security verification required. Please try again.';
            showAlert(errorMessage);
            return { success: false, require_turnstile: true, message: errorMessage };
        }
        if (data.rc === 1 || data.rc === true) {
            console.log('Member: Form validation successful, submitting');
            
            // Handle redirect if present
            if (data.redirect_url || data.redirect) {
                const redirectUrl = data.redirect_url || data.redirect;
                console.log('Redirecting to:', redirectUrl);
                window.location.href = redirectUrl;
                return { success: true, redirected: true };
            }
            
            // Process HTML content replacement if present
            if (data.html_content) {
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: true, replaced: true };
            }
            
            // Standard form submission
            if (form.getAttribute('action')) {
				const inIframe = (() => {
					try {
						return window.self !== window.top;
					} catch (e) {
						return true;
					}
				})();

				if (inIframe) {
					const actionUrl = form.getAttribute('action');
					headers['X-Iframe'] = '1';
					const resp = await axios.post(actionUrl, formDataStr, { headers: headers });
					const finalUrl = resp?.request?.responseURL;
					window.location.href = finalUrl || actionUrl;
					return { success: true, submitted: true, redirected: true };
				}

                form.submit();
                return { success: true, submitted: true };
            }
            
            return { success: true };
        } else {
            // Form validation failed
            console.log('Member: Form validation failed', data.rm);
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Check if server returned updated HTML content (e.g., with error messages)
            if (data.html_content) {
                console.log('Member: Server returned updated HTML content for validation failure');
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: false, replaced: true };
            }
            
            // Get the exact error message from the server response
            let errorMessage = '表單驗證失敗';
            if (data.rm) {
                errorMessage = data.rm; // Use the exact message from the server
                console.log('Server validation error message:', errorMessage);
            }
            
            // Show error message directly in alert without translation
            showAlert(errorMessage);
            
            // Return error info
            return { success: false, message: errorMessage };
        }
    } catch (error) {
        console.error('Member: Form submission error', error);
        
        // Reset button state
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'off');
            submitButton.disabled = false;
        }
        
        // Show error message
        const errorMessage = translate('系統發生錯誤，請稍後再試。', '系統發生錯誤，請稍後再試。');
        showAlert(errorMessage);
        
        throw error; // Re-throw to ensure NonSE.js catches it for proper display
    }
};

// Export a default object as expected by index.js
export default {
    submitForm,
    useFormSubmission,
    createGlobalCheckForm
};
